import React from 'react'

import { Progress } from 'antd'
import { HStack, Link, Text, VStack } from 'govwell-ui'
import { AICommunityAssistantUsageQuotaStatus } from 'types/graphql'

import AIAssistantUpsellModal from 'src/components/AIAssistant/AIAssistantUpsellModal'
import { useAICommunityAssistantUsageQuery } from 'src/fetch/aiChat'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'

const ProgressBarColorForQuotaStatusMap: Record<AICommunityAssistantUsageQuotaStatus, string> = {
  UnderQuota: 'var(--color-blue-500)',
  NearOrAtQuota: 'var(--color-yellow-500)',
  OverQuota: 'var(--color-red-500)',
}

const AICommunityAssistantUsageFooter = () => {
  const organizationId = useMyOrgId()
  const { aiCommunityAssistantUsage } = useAICommunityAssistantUsageQuery({
    organizationId,
    date: { type: 'ThisMonth' },
  })

  const upsellModalState = useDisclosure()

  if (!aiCommunityAssistantUsage) {
    return null
  }

  const { quotaDetails, usedResponses, tiers } = aiCommunityAssistantUsage
  // Unlimited-quota tiers (Enterprise, Reverse Trial) intentionally omit
  // quotaDetails — render a bare count + pricing link instead of the
  // progress bar so staff still see usage and have a path to view pricing.
  const isOverQuota = quotaDetails?.status === 'OverQuota'
  const linkText = quotaDetails
    ? isOverQuota
      ? 'Upgrade to continue access'
      : 'Get unlimited responses'
    : 'View pricing'

  return (
    <VStack>
      {quotaDetails && (
        <Progress
          percent={quotaDetails.percentUsed}
          showInfo={false}
          strokeColor={ProgressBarColorForQuotaStatusMap[quotaDetails.status]}
          className="m-0"
        />
      )}
      <HStack gap={2} align="center" className="px-3 py-1.5">
        <Text.Body>
          {quotaDetails ? (
            <>
              <strong>{quotaDetails.usedResponses}</strong>/{quotaDetails.allowedResponses} AI
              responses used this month
            </>
          ) : (
            <>
              <strong>{usedResponses}</strong> AI responses this month
            </>
          )}
        </Text.Body>
        <span className="text-gray-400">·</span>
        <Link onClick={upsellModalState.open}>{linkText}</Link>
      </HStack>
      {upsellModalState.isOpen && (
        <AIAssistantUpsellModal onClose={upsellModalState.close} tiers={tiers} />
      )}
    </VStack>
  )
}

export default React.memo(AICommunityAssistantUsageFooter)
